import { connect, useDispatch } from 'react-redux';
import { ChangeEvent, useEffect, useState } from 'react';
import { Dispatch } from 'redux';

import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';

import { Modal } from '../../../UIPackage/ModalModule/components/Modal';
import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { Textarea } from '../../../UIPackage/MainModule/components/Textarea';
import styles from './ErknmCopyModal.module.scss';
import { InfoBlock } from '../../../CorePackage/MainModule/components/InfoBlock';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { knmClearInvalidFields, knmGetInvalidFields, knmSetUrlId } from '../actions/knmActions';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { getKnmData, getKnmFormRef, getKnmId, getKnmInvalidFields } from '../selectors/knmSelectors';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { IInvalidField, knmInvalidFields } from '../constants/knmInvalidFields';
import { CreateErknmModalNew } from '../../../KnmErknmPackage/MainModule/components/CreateErknmModalNew';
import { KnmInvalidFieldEnum } from '../enums/KnmInvalidFieldEnum';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import { IKnmForm } from '../interfaces/IKnmForm';
import { uuidSubstringRegExp } from '../constants/uuidSubstringRegExp';
import { IErknmCopyResponse } from '../../../APIPackage/KnmModule/interfaces/IErknmCopyResponse';
import { fetchErknmCopy } from '../../../APIPackage/KnmModule/services/knmApi';
import { coreSetLoading } from '../../../CorePackage/MainModule/actions/coreActions';
import { ROUTE_KNM } from '../../../CorePackage/MainModule/routes/routes';
import { convertFormDateValueToKnmDataDate } from '../common/convertFormDateValueToKnmDataDate';
import { convertFormTimeValueToKnmDataTime } from '../common/convertFormTimeValueToKnmDataTime';
import { pmInvalidFields } from '../constants/pmInvalidFields';
import { PmInvalidFieldEnum } from '../enums/PmInvalidFieldEnum';

interface IErknmCopyModalProps {
  visible: boolean;
  onClose: () => void;
}

interface IErknmCopyModalStoreProps {
  knmId: number | undefined;
  formRef: IKnmFormRef | undefined;
  invalidFields: string[] | undefined;
  knmData: IKnm | undefined;
}

const npaIdRegExp = /\d+/g;
const seIdRegExp = /\d+\|\d+/g;

const getPrettyPrintInvalidFields = (invalidFields: string[] | undefined, isPm: boolean): IInvalidField[] => {
  if (!invalidFields) {
    return [];
  }

  return invalidFields
    .map(invalidField => {
      const fieldPath = invalidField
        .replaceAll(uuidSubstringRegExp, '{id}')
        .replaceAll(seIdRegExp, '{id}')
        .replaceAll(npaIdRegExp, '{id}');

      return isPm
        ? pmInvalidFields[fieldPath as PmInvalidFieldEnum]
        : knmInvalidFields[fieldPath as KnmInvalidFieldEnum];
    })
    .filter(item => item);
};

const ErknmCopyModalWithoutStore = ({
  visible,
  onClose,
  knmId,
  formRef,
  invalidFields,
  knmData,
}: IErknmCopyModalProps & IErknmCopyModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [copyReason, setCopyReason] = useState('');
  const [createModalInitialValues, setCreateModalInitialValues] = useState<IKnmForm | undefined>();
  const isPm = !!formRef?.current.getState().values.pmType;
  const prettyPrintInvalidFields = getPrettyPrintInvalidFields(invalidFields, isPm);
  const hasRequiredInvalidField = prettyPrintInvalidFields.some(item => item.required);
  const isCopyEnabled = Boolean(!!copyReason.trim() && Array.isArray(invalidFields) && !hasRequiredInvalidField);

  const [isShowCreateModal, setIsShowCreateModal] = useState(false);

  const handleChangeCopyReason = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCopyReason(e.currentTarget.value);
  };

  const handleContinue = () => {
    const values = formRef?.current.getState().values;
    const initialValues: IKnmForm = {
      knoOrganizationErknm: values?.knoOrganizationErknm,
      prosecutorOrganizationErknm: values?.prosecutorOrganizationErknm,
      kindControl: values?.kindControl,
      typeErknm: values?.typeErknm,
      reasonsErknm: values?.reasonsErknm?.length ? [values.reasonsErknm[0]] : [],
      approveRequired: values?.approveRequired,
      kindKnm: values?.kindKnm,
      invalidFields,
      firstExtractDateTime: values?.firstExtractDateTime || null,
      prosecutorOrganization: values?.prosecutorOrganization,

      pmType: values?.pmType,
      startDate: values?.startDate,
      stopDate: values?.stopDate,
      supervisionTypePm: values?.supervisionTypePm,
      reasonsPm: values?.reasonsPm,
      objectsPm: values?.objectsPm,
      experts: values?.experts,
      location: values?.location,
      fiasRegion: values?.fiasRegion,
      region: values?.region,
      notInFIAS: values?.notInFIAS,
      manualAddress: values?.manualAddress,
      interactionCategory: values?.interactionCategory,
      isHasCollaboratingOrganization: values?.isHasCollaboratingOrganization,
      collaboratingOrganizationsErknm: values?.collaboratingOrganizationsErknm,
      docTarget: values?.docTarget?.map(item => ({
        ...item,
        file: undefined,
      })),
      ogrn: values?.ogrn,
      isUnableToRefuse: values?.isUnableToRefuse,

      inspectorsErknm: values?.inspectorsErknm,
      eventsPm: values?.eventsPm?.map(item => ({
        ...item,
        startDate: undefined,
        stopDate: undefined,
      })),

      videoCallInfo: values?.videoCallInfo,
      requirementsNpa: values?.requirementsNpa,
      subjectTypePm: values?.subjectTypePm,
    };

    if (!initialValues.pmType) {
      setCreateModalInitialValues(initialValues);
      setIsShowCreateModal(true);
    } else {
      sessionStorage.setItem('initialValuesPm', JSON.stringify(initialValues));
      sessionStorage.setItem('initialValuesPmErknm', JSON.stringify(knmData?.pmErknm));
      sessionStorage.setItem('initialInvalidFields', JSON.stringify(invalidFields));
      window.location.href = '/private/pm/new';
    }
  };

  const handleClose = () => {
    setCopyReason('');
    dispatch(knmClearInvalidFields());
    onClose();
  };

  useEffect(() => {
    if (visible && typeof knmId === 'number') {
      dispatch(knmGetInvalidFields(knmId));
    }
  }, [dispatch, knmId, visible]);

  const handleCopy = async (values: IKnmForm) => {
    const copyResponse: IErknmCopyResponse = {
      sourceKnmId: typeof knmId === 'number' ? knmId : null,
      copyReason,
      knoOrganizationId: values.knoOrganizationErknm?.value.dictVersionId || null,
      prosecutorOrganizationErvkUuid: values.prosecutorOrganizationErknm?.ervkUuid || null,
      prosecutorOrganizationErvkVersionUuid: values.prosecutorOrganizationErknm?.ervkVersionUuid || null,
      kindControlId: values.kindControl?.value.dictVersionId || null,
      typeId: typeof values.typeErknm?.id === 'number' ? values.typeErknm.id : null,
      reasonErvkUuid: values.reasonsErknm?.[0]?.ervkUuid || null,
      approveRequired: values.approveRequired?.value || null,
      kindKnmId: values.kindKnm?.value.dictVersionId || null,
      startDate: convertFormDateValueToKnmDataDate(values.startDate),
      startTime: convertFormTimeValueToKnmDataTime(values.startTime),
      stopDate: convertFormDateValueToKnmDataDate(values.stopDate),
      stopTime: convertFormTimeValueToKnmDataTime(values.stopTime),
    };

    dispatch(coreSetLoading(true));
    fetchErknmCopy(copyResponse)
      .then(createdKnmId => {
        dispatch(coreSetLoading(false));
        window.history.pushState(null, '', ROUTE_KNM(createdKnmId));
        dispatch(knmSetUrlId(createdKnmId.toString()));
        window.location.reload();
      })
      .catch(() => dispatch(coreSetLoading(false)));
  };

  return (
    <>
      <Modal visible={visible && !isShowCreateModal} onClose={handleClose}>
        {isPm && <ModalHeader title="Создание копии ПМ" />}
        {!isPm && <ModalHeader title="Создание копии КНМ" />}
        <ModalBody>
          <Row>
            <InfoBlock>
              {isPm
                ? 'При создании копии ПМ будет создан новый паспорт ПМ, который необходимо будет подписать электронной подписью'
                : 'При создании копии КНМ будет создан новый паспорт КНМ, который необходимо будет подписать электронной подписью'}
            </InfoBlock>
          </Row>
          <Row>
            <FieldBlock label={isPm ? 'Укажите основание создания копии ПМ' : 'Укажите основание создания копии КНМ'}>
              <Textarea
                value={copyReason}
                onChange={handleChangeCopyReason}
                inputClassName={styles.CopyReasonTextarea}
                resize="none"
              />
            </FieldBlock>
          </Row>
          {!!invalidFields?.length && (
            <Row>
              <InfoBlock color={hasRequiredInvalidField ? 'red' : 'yellow'} iconVerticalPosition="top">
                <div>
                  В паспорте используются справочные значения, которые не соответствуют актуальным значениям ЕРВК и ФГИС
                  РОТ:
                  <br />
                  {prettyPrintInvalidFields.map((value, index) => (
                    <span key={index} className={styles.InvalidFieldName}>
                      {value.name}
                    </span>
                  ))}
                  {hasRequiredInvalidField
                    ? 'Создание копии паспорта невозможно.'
                    : 'В новом паспорте они будут выделены как ошибочные.'}
                </div>
              </InfoBlock>
            </Row>
          )}
        </ModalBody>
        <ModalActions>
          <Button
            text="Продолжить"
            color="primary"
            size="medium"
            type="button"
            disabled={!isCopyEnabled}
            onClick={handleContinue}
          />
          <Button text="Отмена" size="medium" type="button" onClick={handleClose} />
        </ModalActions>
      </Modal>
      <CreateErknmModalNew
        onClose={() => setIsShowCreateModal(false)}
        onSaveButtonClick={handleCopy}
        visible={isShowCreateModal}
        initialModalValues={createModalInitialValues}
        invalidFields={invalidFields}
        isCopyEnabled={isCopyEnabled}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmCopyModalStoreProps => ({
  knmId: getKnmId(state),
  formRef: getKnmFormRef(state),
  invalidFields: getKnmInvalidFields(state),
  knmData: getKnmData(state),
});

export const ErknmCopyModal = connect(mapStateToProps)(ErknmCopyModalWithoutStore);
